import { useTranslation } from "react-i18next";
import { CheckCircle2, Hourglass, Ban, X } from "lucide-react";
import Button from "../../../components/ui/Button";

interface Props {
  count: number;
  onClear: () => void;
  onMarkPaid: () => void;
  onExtendTrial: () => void;
  onRevoke: () => void;
}

/**
 * Sticky strip above the companies table when one or more rows are selected.
 * Exposes the three bulk operator actions and a way to clear the selection.
 */
export default function BulkActionsBar({
  count,
  onClear,
  onMarkPaid,
  onExtendTrial,
  onRevoke,
}: Props) {
  const { t } = useTranslation();

  if (count <= 0) return null;

  return (
    <div
      role="region"
      aria-label={t("Bulk actions")}
      className="sticky top-0 z-20 rounded-xl border border-primary/30 bg-card shadow-sm ring-1 ring-primary/10 px-3 py-2.5 flex flex-wrap items-center gap-2"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold tabular-nums">
          {count}
        </span>
        <span className="text-sm font-medium text-foreground truncate">
          {t("{{count}} companies selected", { count })}
        </span>
        <button
          type="button"
          onClick={onClear}
          className="ms-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-1.5 py-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("Clear selection")}
        >
          <X size={12} aria-hidden="true" />
          {t("Clear")}
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="success" onClick={onMarkPaid}>
          <CheckCircle2 size={14} className="me-1.5" />
          {t("Mark as Paid")}
        </Button>
        <Button size="sm" variant="warning" onClick={onExtendTrial}>
          <Hourglass size={14} className="me-1.5" />
          {t("Extend Trial")}
        </Button>
        <Button size="sm" variant="destructive" onClick={onRevoke}>
          <Ban size={14} className="me-1.5" />
          {t("Revoke")}
        </Button>
      </div>
    </div>
  );
}
